import React, { ComponentProps } from 'react';
import styled from 'styled-components';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';

interface Props extends React.PropsWithChildren {
  value: any[];
  totalRecords: number;
  pageIndex: number;
  pageSize: number;
  onPageChange(event: { pageIndex: number; pageSize: number }): void;
  pageSizeOptions: number[];
  style?: React.CSSProperties;
  tableStyle?: React.CSSProperties;
  scrollable?: boolean;
  loading?: boolean;
  id?: string;
  className?: string;
  rowClassName?: ComponentProps<typeof DataTable>['rowClassName'];
}

const AdminSearchResultsGrid: React.FC<Props> = ({
  value,
  totalRecords,
  pageIndex,
  pageSize,
  onPageChange,
  pageSizeOptions,
  style,
  tableStyle,
  scrollable,
  loading,
  id,
  className,
  rowClassName,
  children,
}) => {
  const paginatorTemplate = {
    layout: 'RowsPerPageDropdown CurrentPageReport PrevPageLink PageLinks NextPageLink',
    RowsPerPageDropdown: (options: any) => (
      <>
        <span style={{ marginRight: '0' }}>Show:</span>
        <Dropdown
          value={options.value}
          options={options.options.map((opt: { label: string; value: number }) => ({
            label: `${opt.label} items`,
            value: opt.value,
          }))}
          onChange={(e) =>
            options.onChange({
              originalEvent: e.originalEvent,
              value: e.value,
            })
          }
          className="paginator-dropdown"
        />
        <span style={{ marginLeft: 10 }}>Total count: {totalRecords}</span>
      </>
    ),
    CurrentPageReport: <span style={{ marginLeft: 'auto' }} />,
  };

  return (
    <StyledAdminDataTable
      value={value}
      style={style}
      tableStyle={tableStyle}
      scrollable={scrollable}
      lazy
      loading={loading}
      paginator
      totalRecords={totalRecords}
      first={pageIndex * pageSize}
      rows={pageSize}
      onPage={(ev) => onPageChange?.({ pageIndex: ev.page ?? 0, pageSize: ev.rows })}
      rowsPerPageOptions={pageSizeOptions}
      paginatorTemplate={paginatorTemplate}
      id={id}
      className={className}
      rowClassName={rowClassName}
    >
      {children}
    </StyledAdminDataTable>
  );
};

export default AdminSearchResultsGrid;

const StyledAdminDataTable = styled(DataTable)`
  .p-datatable-scrollable-table > .p-datatable-thead {
    z-index: unset;
  }

  &.p-datatable .p-frozen-column::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 5px;
    height: 100%;
    pointer-events: none;
  }

  &&& .paginator-dropdown {
    padding: 0;
    align-items: center;
    border: none;
    outline: none;
    border-radius: 2px;
    box-shadow: none;
    font-size: 12px;
    font-weight: 600;
    font-family: 'Inter';
    & .p-inputtext {
      padding: 0;
      color: ${(props) => props.theme.colors.primaryBlack};
      justify-content: center;
    }
    & .p-dropdown-trigger {
      color: ${(props) => props.theme.colors.primaryBlack};
      width: 10px;
      justify-content: center;
      margin-left: 8px;
    }
  }

  &&& .paginator-dropdown.p-dropdown:not(.p-disabled).p-focus {
    outline: none !important;
    box-shadow: none;
    border: none;
  }

  .p-paginator {
    background-color: ${(props) => props.theme.colors.primaryWhite};
    padding: 0 0 0.25rem 0;
    ${(props) => props.theme.typography.contentTableBodyXs};
    border: 0;
    margin-top: 20px;
  }

  &&& .p-datatable-table {
    border-collapse: collapse;
  }
  .p-datatable-header {
    background-color: ${(props) => props.theme.colors.elementsFilledInput};
    padding: 12px 1rem;
    align-items: center;
  }
  .p-datatable-thead > tr > th {
    ${(props) => props.theme.typography.contentTableTitleXs}
    background-color: ${(props) => props.theme.colors.elementsFilledInput};
    color: ${(props) => props.theme.colors.primaryBlack70};
    padding: 0.5rem 1rem;
    font-size: 14px;
    height: 50px;
    border: 0;
  }
  .p-datatable-tbody > tr > td,
  .p-datatable-tfoot > tr > td {
    ${(props) => props.theme.typography.contentTableBodyXs}
    font-size: 14px;
    padding: 16px;
    height: 50px;
    border-width: 0 0 1px 0;
  }

  .p-datatable-tbody > tr:has(td:hover) {
    background-color: #e8f1e9;
  }

  .p-datatable-tbody > tr.p-disabled {
    background-color: ${(props) => props.theme.colors.elementsTableRow};
    opacity: 1;
    user-select: auto;
  }
  .p-datatable-tbody > tr.p-disabled * {
    user-select: auto;
  }

  && tfoot > tr > td.p-row-total {
    font-weight: 600;
    background-color: ${(props) => props.theme.colors.elementsTableRow};
  }

  && tfoot > tr > td.p-row-note {
    font-weight: 400;
    background-color: ${(props) => props.theme.colors.elementsTableRow};
  }

  & .p-datatable-tbody > tr > td.p-editable-column.p-cell-editing,
  &.p-datatable-gridlines .p-datatable-tfoot > tr > td:has(input.p-inputtext:enabled:focus),
  &.p-datatable-gridlines .p-datatable-tfoot > tr > td:last-child:has(input:enabled:focus) {
    padding: 0;
    & > .p-inputwrapper.p-inputwrapper-focus,
    .p-inputwrapper.p-inputwrapper-focus input,
    .p-dropdown,
    input.p-inputtext,
    .p-focus {
      background-color: transparent;
      border: 0;
      outline: none;
      box-shadow: none;
    }
    box-shadow: inset 0 0 1px 1px ${(props) => props.theme.colors.primaryOrange};
  }

  & .p-datatable-tbody > tr.p-highlight {
    background-color: var(--row-selection-color);
  }

  && .p-inputtext {
    font-size: 12px;
  }
  && .p-inputtext:enabled:focus {
    border-color: ${(props) => props.theme.colors.primaryOrange};
    box-shadow: 0 0 0 0.2rem rgba(231, 81, 19, 0.5);
  }

  && .p-dropdown:not(.p-disabled).p-focus {
    outline: 0 none;
    outline-offset: 0;
    border-color: ${(props) => props.theme.colors.primaryOrange};
    box-shadow: 0 0 0 0.2rem rgba(231, 81, 19, 0.5);
  }

  && .p-dropdown-item {
    overflow: scroll;
  }

  && .p-datatable-loading-overlay {
    background-color: rgba(255, 255, 255, 0.6);
  }

  /* HACK #7592: This fixes issue with Tab key navigation for tables with horizontal scrollbar: browser in attempt to focus on a cell, shifts the entire table */
  a.p-cell-editor-key-helper.p-hidden-accessible {
    position: relative;
    display: block;
    width: 0;
    height: 0;
  }

  /* HACK: sub/sup are changing lines gap by default - we want all lines to have the same height and align properly */
  && sub {
    vertical-align: baseline;
    position: relative;
    top: 0.33em;
  }
  && sup {
    vertical-align: baseline;
    position: relative;
    top: -0.33em;
  }
`;
